import type { components } from "@/__openapi__/backend/schema";
import { MEM_SPACE_ACCOUNT_ID } from "@/store/contacts/constants";
import { resolveLastAction, type LastActionModelData } from "./resolve-last-action";
import { formatPreviewAttribution } from "./format-preview-attribution";

type NoteArtifactPayload =
  | components["schemas"]["NoteCreatedV1ArtifactPayload"]
  | components["schemas"]["NoteUpdatedV1ArtifactPayload"];

// The backend may ship "Mem AI Assistant"; the product surfaces "Mem".
const MEM_DISPLAY_NAME = "Mem";

/**
 * Builds the chat note-artifact preview's footer attribution line
 * ("Created just now by Mem", "Edited Aug 21 by Jess Joules") from a server
 * artifact payload. Reuses the same resolveLastAction core as the floating
 * preview so the two surfaces always agree.
 *
 * Returns null when the payload lacks the attribution fields (e.g. an older
 * backend that hasn't shipped them yet) — callers fall back to the existing
 * secondary_label in that case.
 *
 * `currentSpaceAccountId` is optional: when provided and the winning actor is
 * the current user, the name renders as "You". The MDS session subsystem is
 * store-free, so it's typically absent here and the real display name is shown.
 */
export const formatArtifactAttribution = (
  payload: NoteArtifactPayload,
  currentSpaceAccountId?: string
): string | null => {
  const attributedCreator = payload.attributed_created_by_space_account_id;
  const createdBy = payload.created_by_space_account_id;
  const ownedBy = payload.owned_by_space_account_id;

  // Need at least one creator-chain id + a created timestamp to attribute.
  const createdActorId = attributedCreator ?? createdBy ?? ownedBy;
  if (!createdActorId) {
    return null;
  }

  const modelData = {
    attributed_created_by_space_account_id: attributedCreator ?? null,
    created_by_space_account_id: createdBy ?? null,
    owned_by_space_account_id: ownedBy ?? createdActorId,
    last_modified_by_space_account_id: payload.last_modified_by_space_account_id ?? null,
    locally_modified_at: payload.note_locally_modified_at ?? payload.note_updated_at,
    quota_created_at: payload.note_quota_created_at ?? payload.note_created_at,
    locally_created_at: payload.note_locally_created_at ?? payload.note_created_at,
    created_at: payload.note_created_at,
    shared_with_me_at: payload.shared_with_me_at ?? undefined,
    shared_with_me_by_space_account_id: payload.shared_with_me_by_space_account_id ?? undefined,
  } satisfies LastActionModelData;

  const latest = resolveLastAction({
    modelData,
    lastModifierSpaceAccountId: payload.last_modified_by_space_account_id ?? undefined,
  });
  if (!latest) {
    return null;
  }

  const authorName = resolveActorName(latest.actorId, payload, currentSpaceAccountId);
  if (!authorName) {
    return null;
  }

  return formatPreviewAttribution({
    verb: latest.verb,
    dateInput: latest.at,
    authorName,
  });
};

const resolveActorName = (
  actorId: string,
  payload: NoteArtifactPayload,
  currentSpaceAccountId?: string
): string | null => {
  if (currentSpaceAccountId && actorId === currentSpaceAccountId) {
    return "You";
  }
  if (actorId === MEM_SPACE_ACCOUNT_ID) {
    return MEM_DISPLAY_NAME;
  }
  const name = payload.actor_display_names?.[actorId];
  if (!name) {
    return null;
  }
  // Normalize the assistant's backend profile name to the product name.
  return name === "Mem AI Assistant" ? MEM_DISPLAY_NAME : name;
};
